import { useEffect, useState } from 'react'
import {
  Search, ChevronRight, ArrowLeft, Pencil, Trash2, Phone, Mail, Building2, X,
} from 'lucide-react'
import MobileLayout from './MobileLayout'
import { apiService, Contact, CustomField, CustomFieldOption } from '@/services/api'
import { useToast } from '@/hooks/use-toast'
import { capitalizeFirstName } from '@/utils/name-format'

type CustomFieldDef = CustomField & { options?: CustomFieldOption[] }
type ContactDetail = Contact & {
  company_name?: string
  custom_fields?: { field: { id: number; name: string }; value?: string }[]
}

const initials = (a?: string, b?: string) =>
  `${a?.[0] ?? ''}${b?.[0] ?? ''}`.toUpperCase() || '?'

export default function MobileContacts() {
  const { toast } = useToast()
  const [contacts, setContacts] = useState<(Contact & { company_name?: string })[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  // Détail
  const [detail, setDetail] = useState<ContactDetail | null>(null)
  // Formulaire (création/édition)
  const [formOpen, setFormOpen] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', linkedin_url: '', company_id: undefined as number | undefined })
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([])
  const [customValues, setCustomValues] = useState<Record<number, string>>({})
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([])
  const [confirmDelete, setConfirmDelete] = useState<ContactDetail | null>(null)

  const loadContacts = async () => {
    setLoading(true)
    try {
      const res = await apiService.getContacts({ page: 1, limit: 100, search: search || undefined, sort: 'last_name', order: 'asc' })
      if (res.success && res.data) setContacts(res.data.data as any)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const t = setTimeout(loadContacts, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const openDetail = async (id: number) => {
    const res = await apiService.getContact(id)
    if (res.success && res.data) setDetail(res.data as ContactDetail)
  }

  const openCreate = async () => {
    setEditId(null)
    setForm({ first_name: '', last_name: '', email: '', phone: '', linkedin_url: '', company_id: undefined })
    setCustomValues({})
    setFormOpen(true)
    const [cf, cos] = await Promise.all([
      apiService.getContactCustomFields(),
      apiService.getCompanies({ page: 1, limit: 1000, sort: 'name', order: 'asc' }),
    ])
    if (cf.success && cf.data) setCustomFields(cf.data)
    if (cos.success && cos.data) setCompanies(cos.data.data.map((c: any) => ({ id: c.id, name: c.name })))
  }

  const openEdit = async (c: ContactDetail) => {
    setEditId(c.id)
    setForm({
      first_name: c.first_name, last_name: c.last_name,
      email: c.email || '', phone: c.phone || '', linkedin_url: c.linkedin_url || '', company_id: c.company_id,
    })
    const vals: Record<number, string> = {}
    c.custom_fields?.forEach((cf) => { if (cf.value) vals[cf.field.id] = cf.value })
    setCustomValues(vals)
    setDetail(null)
    setFormOpen(true)
    const [cf, cos] = await Promise.all([
      apiService.getContactCustomFields(),
      apiService.getCompanies({ page: 1, limit: 1000, sort: 'name', order: 'asc' }),
    ])
    if (cf.success && cf.data) setCustomFields(cf.data)
    if (cos.success && cos.data) setCompanies(cos.data.data.map((c2: any) => ({ id: c2.id, name: c2.name })))
  }

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast({ variant: 'destructive', title: 'Champs requis', description: 'Prénom, nom et email sont obligatoires.' })
      return
    }
    setSaving(true)
    try {
      const payload: any = {
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone || undefined,
        linkedin_url: form.linkedin_url || undefined,
        company_id: form.company_id,
        custom_fields: Object.entries(customValues)
          .filter(([, v]) => v !== '' && v != null)
          .map(([k, v]) => ({ contacts_custom_field_id: parseInt(k, 10), value: v })),
      }
      const res = editId ? await apiService.updateContact(editId, payload) : await apiService.createContact(payload)
      if (res.success) {
        toast({ title: editId ? 'Contact modifié' : 'Contact créé', variant: 'success' })
        if (res.avertissementUpRegistre) {
          toast({ variant: 'destructive', title: 'Registre des visiteurs non mis à jour', description: res.avertissementUpRegistre })
        }
        setFormOpen(false)
        loadContacts()
      } else {
        toast({ variant: 'destructive', title: 'Erreur', description: res.error || 'Échec de l\'enregistrement.' })
      }
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    if (!confirmDelete) return
    const res = await apiService.deleteContact(confirmDelete.id)
    if (res.success) {
      toast({ title: 'Contact mis à la corbeille', variant: 'success' })
      setConfirmDelete(null)
      setDetail(null)
      loadContacts()
    } else {
      toast({ variant: 'destructive', title: 'Erreur', description: 'Suppression impossible.' })
    }
  }

  return (
    <MobileLayout title="Contacts" onAdd={openCreate}>
      {/* Recherche */}
      <div className="px-4 py-3 sticky top-0 bg-white z-10 border-b border-gray-50">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl bg-gray-100 border-0 pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Rechercher un contact…"
          />
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
        </div>
      ) : contacts.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-16">Aucun contact</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {contacts.map((c) => (
            <button key={c.id} onClick={() => openDetail(c.id)} className="w-full flex items-center gap-3 px-4 py-3 active:bg-gray-50 text-left">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {initials(c.first_name, c.last_name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 truncate">{c.first_name} {c.last_name}</p>
                <p className="text-xs text-blue-600 truncate">{c.company_name || 'Aucune entreprise'}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Overlay détail */}
      {detail && (
        <div className="fixed inset-0 z-30 bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-100 shrink-0">
            <button onClick={() => setDetail(null)} className="p-1.5 text-gray-500"><ArrowLeft className="w-5 h-5" /></button>
            <span className="font-semibold text-gray-700 text-sm">Détail</span>
            <div className="ml-auto flex gap-1">
              <button onClick={() => openEdit(detail)} className="p-1.5 text-gray-500"><Pencil className="w-5 h-5" /></button>
              <button onClick={() => setConfirmDelete(detail)} className="p-1.5 text-rose-500"><Trash2 className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-5 pt-6 pb-8 text-center text-white">
              <div className="w-20 h-20 rounded-2xl bg-white/15 ring-2 ring-white/30 mx-auto flex items-center justify-center text-2xl font-bold">
                {initials(detail.first_name, detail.last_name)}
              </div>
              <h2 className="text-xl font-bold mt-3">{detail.first_name} {detail.last_name}</h2>
              <span className="inline-flex items-center gap-1 mt-1 px-2.5 py-1 rounded-full bg-white/20 text-sm">
                <Building2 className="w-3.5 h-3.5" /> {detail.company_name || 'Aucune entreprise'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 p-4">
              <a href={detail.phone ? `tel:${detail.phone}` : undefined} className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm ${detail.phone ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-300 pointer-events-none'}`}>
                <Phone className="w-4 h-4" /> Appeler
              </a>
              <a href={detail.email ? `mailto:${detail.email}` : undefined} className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm ${detail.email ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-300 pointer-events-none'}`}>
                <Mail className="w-4 h-4" /> Email
              </a>
              <a
                href={
                  detail.linkedin_url
                    ? (detail.linkedin_url.startsWith('http') ? detail.linkedin_url : `https://${detail.linkedin_url}`)
                    : `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent([detail.first_name, detail.last_name, detail.company_name].filter(Boolean).join(' '))}&origin=GLOBAL_SEARCH_HEADER`
                }
                target="_blank"
                rel="noopener noreferrer"
                title={detail.linkedin_url ? 'Voir sur LinkedIn' : 'Rechercher sur LinkedIn'}
                className="flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm text-white"
                style={{ background: '#0A66C2' }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>
                <span className="hidden">LinkedIn</span>
              </a>
            </div>
            <div className="px-4 pb-6 space-y-3">
              <div className="rounded-2xl border border-blue-100 bg-blue-50/50 overflow-hidden">
                <div className="px-3 py-2 bg-blue-100/70 text-xs font-bold text-blue-800 uppercase tracking-wide">Coordonnées</div>
                <div className="px-3 py-2.5"><span className="text-xs text-gray-400">Email</span><p className="font-semibold text-gray-800 break-words">{detail.email || 'Non renseigné'}</p></div>
                <div className="px-3 py-2.5 border-t border-blue-100"><span className="text-xs text-gray-400">Téléphone</span><p className="font-semibold text-gray-800">{detail.phone || 'Non renseigné'}</p></div>
                <div className="px-3 py-2.5 border-t border-blue-100">
                  <span className="text-xs text-gray-400">LinkedIn</span>
                  {detail.linkedin_url ? (
                    <a
                      href={detail.linkedin_url.startsWith('http') ? detail.linkedin_url : `https://${detail.linkedin_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold block truncate"
                      style={{ color: '#0A66C2' }}
                    >
                      Voir le profil ↗
                    </a>
                  ) : (
                    <a
                      href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent([detail.first_name, detail.last_name, detail.company_name].filter(Boolean).join(' '))}&origin=GLOBAL_SEARCH_HEADER`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold block"
                      style={{ color: '#0A66C2' }}
                    >
                      Rechercher sur LinkedIn ↗
                    </a>
                  )}
                </div>
              </div>
              {detail.custom_fields && detail.custom_fields.filter((c) => c.value).length > 0 && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50/50 overflow-hidden">
                  <div className="px-3 py-2 bg-amber-100/70 text-xs font-bold text-amber-800 uppercase tracking-wide">Champs personnalisés</div>
                  <div className="divide-y divide-amber-100">
                    {detail.custom_fields.filter((c) => c.value).map((cf) => (
                      <div key={cf.field.id} className="px-3 py-2.5"><span className="text-xs text-gray-400">{cf.field.name}</span><p className="font-semibold text-gray-800">{cf.value}</p></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay formulaire */}
      {formOpen && (
        <div className="fixed inset-0 z-40 bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-100 shrink-0">
            <button onClick={() => setFormOpen(false)} className="p-1.5 text-gray-500"><X className="w-5 h-5" /></button>
            <span className="font-semibold text-gray-800">{editId ? 'Modifier le contact' : 'Nouveau contact'}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <Field label="Prénom *"><input className="inp" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: capitalizeFirstName(e.target.value) })} placeholder="Prénom" /></Field>
            <Field label="Nom *"><input className="inp" style={{ textTransform: 'uppercase' }} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value.toUpperCase() })} placeholder="Nom" /></Field>
            <Field label="Email *"><input className="inp" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@exemple.com" /></Field>
            <Field label="Téléphone"><input className="inp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="01 23 45 67 89" /></Field>
            <Field label="URL LinkedIn"><input className="inp" type="url" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/…" /></Field>
            <Field label="Entreprise">
              <select className="inp" value={form.company_id ?? ''} onChange={(e) => setForm({ ...form, company_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}>
                <option value="">Aucune entreprise</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>

            {customFields.length > 0 && (
              <div className="pt-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Champs personnalisés</p>
                <div className="space-y-3">
                  {customFields.map((cf) => (
                    <Field key={cf.id} label={cf.name}>
                      <CustomInput field={cf} value={customValues[cf.id] || ''} onChange={(v) => setCustomValues({ ...customValues, [cf.id]: v })} />
                    </Field>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-gray-100 p-3 shrink-0">
            <button onClick={save} disabled={saving} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-60">
              {saving ? 'Enregistrement…' : editId ? 'Enregistrer' : 'Créer le contact'}
            </button>
          </div>
        </div>
      )}

      {/* Confirmation suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">Supprimer ce contact ?</h3>
            <p className="text-sm text-gray-500 mt-1">{confirmDelete.first_name} {confirmDelete.last_name} : la fiche ira dans la corbeille, d'où un administrateur pourra la restaurer.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 font-semibold text-gray-700">Annuler</button>
              <button onClick={doDelete} className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white font-semibold">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;border:1px solid #e5e7eb;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.95rem}.inp:focus{outline:2px solid #3b82f6;border-color:transparent}`}</style>
    </MobileLayout>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

function CustomInput({ field, value, onChange }: { field: CustomFieldDef; value: string; onChange: (v: string) => void }) {
  if (field.type === 'radio') {
    return (
      <div className="inline-flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium">
        {field.options?.map((o) => {
          const ov = o.value || o.label
          const sel = value === ov
          const lower = o.label.trim().toLowerCase()
          const cls = lower === 'oui' ? 'bg-emerald-600 text-white' : lower === 'non' ? 'bg-rose-500 text-white' : 'bg-blue-600 text-white'
          return (
            <button key={o.id} type="button" onClick={() => onChange(ov)} className={`px-3 py-1 rounded-md ${sel ? cls : 'text-gray-500'}`}>{o.label}</button>
          )
        })}
      </div>
    )
  }
  if (field.type === 'select') {
    return (
      <select className="inp" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Sélectionner…</option>
        {field.options?.map((o) => <option key={o.id} value={o.value || o.label}>{o.label}</option>)}
      </select>
    )
  }
  const type = field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : field.type === 'url' ? 'url' : 'text'
  return <input className="inp" type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={`Entrez ${field.name.toLowerCase()}`} />
}
